import { useRef, useState } from 'react';

import chatService from '../services/chatService';
import { ChatMessage } from '../types';

/** Suggestion chips offered while the conversation is still short. */
export const SUGGESTION_CHIPS = [
  'Best restaurants nearby?',
  'Spa recommendations',
  'Transportation options',
  'Local activities',
] as const;

/** Opening message seeded into every concierge conversation. */
export const WELCOME_MESSAGE =
  "Welcome to Belhop! I'm your AI concierge. How can I help you today?";

const CONNECTION_ERROR_MESSAGE =
  "I'm having trouble connecting right now. Please try again in a moment.";

const FALLBACK_RESPONSE = "I'm sorry, I couldn't process that request.";

export interface UseConciergeChat {
  messages: ChatMessage[];
  input: string;
  setInput: (value: string) => void;
  isTyping: boolean;
  handleSend: (text?: string) => Promise<void>;
  suggestionChips: readonly string[];
  showSuggestions: boolean;
}

/**
 * Headless concierge-chat logic shared by the desktop `ConciergePanel` sidebar
 * and the mobile bottom sheet. Owns the message list (seeded with the welcome
 * message), the typing indicator, and the `chatService.sendMessageWithData`
 * round-trip including session continuity and connection-error handling.
 *
 * Open/close state lives in `ChatContext` (`useChat`) — deliberately separate
 * from message state so either surface can drive visibility independently.
 */
export function useConciergeChat(): UseConciergeChat {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 1,
      text: WELCOME_MESSAGE,
      sender: 'bot',
      type: 'text',
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const messageIdRef = useRef(2);

  const getNextMessageId = () => messageIdRef.current++;

  const handleSend = async (text?: string) => {
    const messageText = text || input.trim();
    if (!messageText) return;

    const userMessage: ChatMessage = {
      id: getNextMessageId(),
      text: messageText,
      sender: 'user',
      type: 'text',
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const response = await chatService.sendMessageWithData(messageText, sessionId || undefined);

      if (response.session_id) {
        setSessionId(response.session_id);
      }

      const botMessage: ChatMessage = {
        id: getNextMessageId(),
        text: response.response || FALLBACK_RESPONSE,
        sender: 'bot',
        type: 'text',
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages((prev) => [
        ...prev,
        {
          id: getNextMessageId(),
          text: CONNECTION_ERROR_MESSAGE,
          sender: 'bot',
          type: 'text',
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  return {
    messages,
    input,
    setInput,
    isTyping,
    handleSend,
    suggestionChips: SUGGESTION_CHIPS,
    showSuggestions: messages.length <= 2,
  };
}

export default useConciergeChat;
